/**
 * data/repos/authRepo — repositório do fluxo de autenticação público, sem sessão
 * (POST /v1/auth/password-reset).
 *
 * Fica separado de settingsRepo (Req 19) de propósito: settingsRepo é o dono do
 * perfil e das preferências de um usuário JÁ autenticado; requestPasswordReset é
 * anônimo e antecede o login — pertence ao domínio de auth/sessão (sessionRepo é o
 * outro membro dessa família, GET /v1/auth/me), não ao de configurações.
 *
 * É a implementação ÚNICA consumida por dois chamadores:
 *  - SettingsPage (usuário logado pedindo redefinição a partir de "Segurança");
 *  - AzimAuthAdapter.requestPasswordReset (tela de login, "Esqueci minha senha"),
 *    que delega para cá em vez de duplicar o fetch.
 *
 * Endpoint público — NÃO deve carregar Bearer token (anti-enumeração, PBT-03,
 * Req 8.3). O apiClient.post injeta Authorization sempre que _token está setado
 * (comportamento de singleton pós-login), então usamos fetch diretamente, sem
 * nenhum header de autenticação — o mesmo mecanismo que AzimAuthAdapter já usa
 * para /auth/token e /auth/token/refresh.
 *
 * O servidor responde 202 com { status: "accepted" } tanto para e-mail existente quanto para
 * inexistente (anti-enumeração, PBT-03, Req 8.3, Req 10.2) — e é o servidor que garante isso,
 * não o cliente. Esta função NUNCA rejeita por regra de negócio; ela rejeita quando a resposta
 * não é 2xx, o que só acontece por motivo independente do e-mail: tenant mal configurado
 * (404 AUTH-ERR-010), slug ausente (500 AUTH-ERR-090), rate limit (429), indisponibilidade (5xx).
 * Tratar esses status como sucesso seria mostrar "Verifique seu e-mail" para quem nunca vai
 * receber e-mail nenhum — a mesma classe de defeito que este arquivo existe para corrigir.
 *
 * O header X-Tenant-Slug é obrigatório: é por ele que o TenantResolutionMiddleware resolve o
 * tenant. Ele identifica o TENANT, nunca o usuário, então enviá-lo não revela nada sobre a
 * existência de um e-mail — a anti-enumeração continua inteira, garantida pelo 202 uniforme do
 * servidor (pinado em PasswordResetControllerTests, PBT-03).
 */

import { resolveTenantSlug } from "../../services/ApiClient.js";

/** Resposta do POST /v1/auth/password-reset. Sempre "accepted" (anti-enumeração, PBT-03). */
export interface PasswordResetResult {
  readonly status: "accepted";
}

/**
 * Overrides opcionais de baseUrl/fetch — usados por AzimAuthAdapter para reutilizar
 * seu próprio `baseUrl` configurado e seu `fetchImpl` injetável (testabilidade),
 * em vez de reler `import.meta.env` e o `fetch` global.
 */
export interface RequestPasswordResetOptions {
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /**
   * Slug do tenant para o header `X-Tenant-Slug`. O `AzimAuthAdapter` passa o seu — o mesmo que
   * ele já envia em /auth/token, /auth/token/refresh, /auth/2fa/verify e /auth/invitations/accept.
   * Omitido (caso da SettingsPage), cai no slug que o `ApiClient` resolve, que é a fonte única
   * do portal; não existe segunda leitura de env aqui de propósito.
   */
  readonly tenantSlug?: string;
}

/**
 * Solicita redefinição de senha por e-mail via POST /v1/auth/password-reset.
 *
 * Sem `options`, deriva a base URL do mesmo env que o ApiClient usa (mantém
 * coerência com os demais repos) e usa o `fetch` global.
 */
export async function requestPasswordReset(
  email: string,
  options: RequestPasswordResetOptions = {},
): Promise<PasswordResetResult> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? ((import.meta.env["VITE_API_BASE_URL"]) ?? "/v1");
  const tenantSlug = options.tenantSlug?.trim() || resolveTenantSlug();
  const url = new URL(`${baseUrl}/auth/password-reset`, window.location.origin).toString();

  const response = await fetchImpl(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Tenant-Slug": tenantSlug,
    },
    body: JSON.stringify({ email }),
  });

  // Só 2xx é sucesso. Qualquer outro status é falha de configuração, de rate limit ou de
  // disponibilidade — nunca uma informação sobre o e-mail, porque o servidor devolve 202 para
  // e-mail existente e inexistente do mesmo jeito. Continuar como "accepted" aqui converteria
  // um tenant mal configurado em "Verifique seu e-mail" na cara do usuário.
  if (!response.ok) {
    const code = await readErrorCode(response);
    // A mensagem é técnica e nunca ecoa o e-mail: ela vai para log e diagnóstico do operador.
    throw new Error(`password-reset: HTTP ${response.status}${code ? ` ${code}` : ""}`);
  }

  // Normaliza o resultado; o backend sempre devolve status "accepted".
  let parsed: { status?: string } | undefined;
  try {
    parsed = (await response.json()) as { status?: string };
  } catch {
    // corpo vazio ou não-JSON — OK para 202
  }
  return { status: (parsed?.status as "accepted" | undefined) ?? "accepted" };
}

/**
 * Extrai o `code` do envelope de erro dos serviços .NET (ex.: "AUTH-ERR-010"), quando houver.
 * Corpo ausente, não-JSON ou sem `code` devolve `undefined` — o status sozinho já basta para
 * a mensagem de erro.
 */
async function readErrorCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { code?: unknown };
    return typeof body?.code === "string" ? body.code : undefined;
  } catch {
    return undefined;
  }
}
